import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button as BootstrapButton, Dropdown, Image } from "react-bootstrap";
import { InfoCircleFill } from "react-bootstrap-icons";
import { useLocation } from "react-router";
import  { getSafeSvg, getSafeHref } from "../../utils/utils"
import { useAuth } from "../../components/AuthContext/AuthContext";
/* Define a function to dynamically import icons */
const importIcon = (iconName) =>
  import(`react-bootstrap-icons/dist/icons/${iconName}.js`);

/**
 * Primary UI component for user interaction
 */
export const Button = ({
  variant,
  className: userClassName,
  size,
  label,
  onClick,
  icon,
  target,
  isDisabled,
  iconOrder,
  svg,
  type,
  items,
  username,
  initials,
  dropdown,
  hover,
  serverError,
  ...props
}) => {
  const [IconComponent, setIconComponent] = useState(null);
  const [validComponent, setValidComponent] = useState(null);
  const direction = iconOrder === "On left" ? "rtl" : "ltr";
  const location = useLocation();
  const { featureFlag } = useAuth();
  const safeSvg = getSafeSvg(svg);
  /* Load icon dynamically when icon prop changes */
  useEffect(() => {
    if (icon) {
      importIcon(icon)
        .then((module) => {
          setIconComponent(module.default);
          setValidComponent(null);
        })
        .catch(() => {
          setIconComponent(null);
          setValidComponent("Please enter a valid Icon name");
        });
    } else {
      setIconComponent(null);
    }
  }, [icon]);

  const variantClassName = `btn btn--${variant}`;
  const sizeClassName = `btn--${size}`;
  const rel = target ? "noopener noreferrer" : null;

  /* Concatenate the user-provided className with other className names */
  const combinedClassName = `${variantClassName} ${sizeClassName} ${userClassName} ${
    hover ? "" : "no-hover"
  }`;

  return (
    <>
      {type === "dropdown" ? (
                <Dropdown
          className={
            dropdown ? `profile-color notification-dropdown` :  "profile-color"
          }
        >
          <Dropdown.Toggle
            className={combinedClassName}
            id="dropdown-basic"
            initials={initials}
            items={items}
            svg={svg}
            username={username}
            variant={variant}
          >
            {safeSvg && <Image alt="svg" fluid src={safeSvg} />}

            <span>{label}</span>
          </Dropdown.Toggle>

          <Dropdown.Menu className="">
            {username && ( // Render only if username exists
              <Dropdown.Item href="#">
                <span className="profile-circle">{initials}</span>
                {username}
              </Dropdown.Item>
            )}
            {items &&
              items.map((item) => (
                <Dropdown.Item
                  key={item.label}
                  href={item.href ? getSafeHref(item.href) : undefined}
                  onClick={item.onClick}
                >
                  {item.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
  
      ) : (
      <div className={location.pathname === "/login" || label === "Log out" ?"": "search-btn"}>
            {serverError ? (
              <div className="server-error">
                <InfoCircleFill /> {serverError}
              </div>
            ) : (
              ""
            )}
            <BootstrapButton
              className={combinedClassName}
              disabled={isDisabled}
              onClick={onClick}
              rel={rel}
              style={{ outline: "none", direction }}
              svg={svg}
              target={target ? "_blank" : null}
              type={type}
              variant={variant}
              {...props}
            >
              <span>{label}</span>
              {safeSvg && <div className="icon">{safeSvg && <img src={safeSvg} alt="" />}</div>}
              {IconComponent && <IconComponent className="icon-margin" />}
            </BootstrapButton>
        </div>
      )}
      {validComponent && (
        <div className="icon-validation">{validComponent}</div>
      )}
    </>
  );
};

/* Default props for buttons */
Button.defaultProps = {
  variant: "primary",
  size: "medium",
  isDisabled: false,
  iconOrder: "On right",
  hover: true, // Default to enable hover
};

/* PropTypes for buttons */
Button.propTypes = {
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "danger",
    "gray",
    "outline",
    "olive",
    "offwhite",
    "white",
    "light-gray",
    "grey-lilac",
    "yellow",
    "powder-pink",
    "mint",
    "dark-green",
    "raspberry",
    "boots-sso",
  ]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  label: PropTypes.string,
  icon: PropTypes.string,
  svg: PropTypes.string,
  target: PropTypes.bool,
  onClick: PropTypes.func,
  isDisabled: PropTypes.bool,
  className: PropTypes.string,
  iconOrder: PropTypes.oneOf(["On left", "On right"]),
  hover: PropTypes.bool,
  type: PropTypes.string,
  items: PropTypes.array,
  username: PropTypes.string,
  initials: PropTypes.string,
  dropdown: PropTypes.any,
  serverError: PropTypes.string,
};
